// Rendering: replay the vector document onto a 2D canvas. Nothing is ever
// composited into the document — every element is redrawn from its mm
// geometry at whatever px/mm the target needs, so the editor, the live bottle
// preview, the downloaded PNG and a future print file all come from the same
// code. Deterministic: no Date, no randomness.
//
// SSR: no canvas/Image/document is touched at module scope. Functions that
// need one create it lazily and are only called from effects and handlers.
//
// ---- Type sizing (cap height → font px) -------------------------------------
// `TextElement.size_mm` is the CAP HEIGHT (height of "H") in mm, because that
// is how label type is specified and it makes 5 mm look like 5 mm in every
// font. Canvas fonts are sized by the em, so:
//     fontPx = size_mm × pxPerMm ÷ capHeightRatio(face)
// where capHeightRatio = capHeight / em for the drawn face. In the browser the
// ratio is MEASURED once per face after it has loaded
// (measureText("H").actualBoundingBoxAscent at 100 px); until then (and in
// Node) the approximate table in fonts.ts is used.
//
// ---- Line layout ---------------------------------------------------------------
// Lines use CSS line-box maths so the canvas matches the overlaid <textarea>
// editor: each line is `fontPx × line_height` tall and the alphabetic baseline
// sits at  lineTop + (lineAdvance − (ascent + descent)) / 2 + ascent,  with the
// face's font ascent/descent (measured via fontBoundingBoxAscent/Descent, else
// the fonts.ts table). The element height is lines × lineAdvance. We draw on
// the ALPHABETIC baseline rather than `textBaseline = "top"` because "top" is
// the em-box top in Chromium but the ascent line in WebKit, which would shift
// every line differently per browser; the alphabetic baseline is identical
// everywhere.
//
// ---- Letter-spacing ------------------------------------------------------------
// Native `ctx.letterSpacing` where supported (Chromium 99+, Firefox 115+,
// Safari 18.4+); otherwise glyphs are drawn one by one with the spacing added
// (kerning is lost in that fallback only). Widths are always computed the same
// way — unspaced width + spacing × (glyphs − 1) — so wrapping and alignment
// agree across both paths and the trailing spacing never offsets centring.

import { labelScale, type Assets, type BottleSpec, type ImageSource } from "./bottles";
import { dpiToPxPerMm } from "./geometry";
import {
  cssFont,
  getFont,
  isFaceLoaded,
  loadFonts,
  resolveFace,
  type FontFace,
  type FontMetrics,
} from "./fonts";
import { getOrnament, loadOrnaments, ornamentImage } from "./ornaments";
import { PALETTE_HEX, luminance } from "./palette";
import type {
  CanvasData,
  EllipseElement,
  ImageElement,
  LabelElement,
  LineElement,
  OrnamentElement,
  RectElement,
  TextElement,
} from "./schema";

type Ctx = CanvasRenderingContext2D;

/** The small print burned into every bottle mock-up (M4 plan decision 8). */
export const SMALL_PRINT = "Mock-up subject to graphic design approval · SOH GD Wines";

// ---------------------------------------------------------------------------
// Canvas helpers (browser only — call from effects/handlers)
// ---------------------------------------------------------------------------

export function createCanvas(width: number, height: number): HTMLCanvasElement {
  if (typeof document === "undefined") throw new Error("createCanvas is browser-only");
  const c = document.createElement("canvas");
  c.width = Math.max(1, Math.round(width));
  c.height = Math.max(1, Math.round(height));
  return c;
}

export function canvasToBlob(canvas: HTMLCanvasElement, type = "image/png"): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Canvas export failed"))), type);
  });
}

let measureCtx: Ctx | null = null;
function getMeasureCtx(): Ctx | null {
  if (measureCtx) return measureCtx;
  if (typeof document === "undefined") return null;
  measureCtx = document.createElement("canvas").getContext("2d");
  return measureCtx;
}

function supportsLetterSpacing(ctx: Ctx): boolean {
  return "letterSpacing" in ctx;
}

// ---------------------------------------------------------------------------
// Font metrics
// ---------------------------------------------------------------------------

const metricsCache = new Map<string, FontMetrics>();

/** Cap height / ascent / descent (fractions of the em) for the face actually drawn. */
export function faceMetrics(face: FontFace): FontMetrics {
  const key = cssFont(face, 100);
  const cached = metricsCache.get(key);
  if (cached) return cached;
  const fallback = face.font.metrics;
  // Only trust a measurement once the real face is loaded; never cache the fallback.
  if (!isFaceLoaded(face)) return fallback;
  const ctx = getMeasureCtx();
  if (!ctx) return fallback;
  ctx.save();
  ctx.font = key;
  if (supportsLetterSpacing(ctx)) ctx.letterSpacing = "0px";
  const h = ctx.measureText("H");
  ctx.restore();
  const cap = h.actualBoundingBoxAscent / 100;
  const asc = h.fontBoundingBoxAscent;
  const desc = h.fontBoundingBoxDescent;
  const m: FontMetrics = {
    capHeight: cap > 0.3 && cap < 1.2 ? cap : fallback.capHeight,
    ascent: Number.isFinite(asc) && asc > 0 ? asc / 100 : fallback.ascent,
    descent: Number.isFinite(desc) && desc >= 0 ? desc / 100 : fallback.descent,
  };
  metricsCache.set(key, m);
  return m;
}

/** Forget measured metrics (call after fonts finish loading if a render ran early). */
export function resetFontMetrics(): void {
  metricsCache.clear();
}

// ---------------------------------------------------------------------------
// Text layout
// ---------------------------------------------------------------------------

export interface TextLine {
  text: string;
  /** Visual width in px (without trailing letter-spacing). */
  width: number;
  /** Left x of the line in px (label-local). */
  x: number;
  /** Alphabetic baseline y in px (label-local). */
  baseline: number;
}

export interface TextLayout {
  face: FontFace;
  font: string;
  fontPx: number;
  spacingPx: number;
  lineAdvancePx: number;
  lines: TextLine[];
  /** lines × lineAdvance, px. */
  heightPx: number;
}

export function fontPxFor(el: Pick<TextElement, "font" | "weight" | "italic" | "size_mm">, pxPerMm: number): number {
  const face = resolveFace(el.font, el.weight, el.italic);
  return (el.size_mm * pxPerMm) / faceMetrics(face).capHeight;
}

function glyphs(s: string): string[] {
  return Array.from(s);
}

function runWidth(ctx: Ctx, s: string, spacingPx: number): number {
  if (!s) return 0;
  const n = glyphs(s).length;
  return ctx.measureText(s).width + spacingPx * Math.max(0, n - 1);
}

/** Greedy word wrap; words longer than the line are broken by character. */
function wrapParagraph(ctx: Ctx, para: string, maxW: number, spacingPx: number): string[] {
  if (para === "") return [""];
  const words = para.split(/(\s+)/).filter((w) => w.length > 0);
  const lines: string[] = [];
  let line = "";
  const pushWord = (word: string) => {
    // Break an over-long word across lines.
    let rest = word;
    while (runWidth(ctx, rest, spacingPx) > maxW && glyphs(rest).length > 1) {
      const gs = glyphs(rest);
      let n = gs.length - 1;
      while (n > 1 && runWidth(ctx, gs.slice(0, n).join(""), spacingPx) > maxW) n--;
      lines.push(gs.slice(0, n).join(""));
      rest = gs.slice(n).join("");
    }
    line = rest;
  };
  for (const w of words) {
    const isSpace = /^\s+$/.test(w);
    if (line === "") {
      if (isSpace) continue; // no leading spaces on wrapped lines
      pushWord(w);
      continue;
    }
    const candidate = line + w;
    if (isSpace || runWidth(ctx, candidate.trimEnd(), spacingPx) <= maxW) {
      line = candidate;
    } else {
      lines.push(line.trimEnd());
      pushWord(w);
    }
  }
  lines.push(line.trimEnd());
  return lines;
}

/**
 * Lay a text element out at `pxPerMm`. Label-local px (origin = label top-left).
 * `ctx` is only used for measuring; its font/letterSpacing are set and restored.
 */
export function layoutText(ctx: Ctx, el: TextElement, pxPerMm: number): TextLayout {
  const face = resolveFace(el.font, el.weight, el.italic);
  const m = faceMetrics(face);
  const fontPx = (el.size_mm * pxPerMm) / m.capHeight;
  const font = cssFont(face, fontPx);
  const spacingPx = el.letter_spacing_em * fontPx;
  const lineAdvancePx = fontPx * el.line_height;
  const boxX = el.x * pxPerMm;
  const boxY = el.y * pxPerMm;
  const boxW = Math.max(1, el.width * pxPerMm);

  ctx.save();
  ctx.font = font;
  if (supportsLetterSpacing(ctx)) ctx.letterSpacing = "0px";
  const raw = el.text.replace(/\r\n?/g, "\n").split("\n");
  const texts = raw.flatMap((p) => wrapParagraph(ctx, p, boxW, spacingPx));
  const halfLeading = (lineAdvancePx - (m.ascent + m.descent) * fontPx) / 2;
  const lines: TextLine[] = texts.map((t, i) => {
    const width = runWidth(ctx, t, spacingPx);
    const x =
      el.align === "left"
        ? boxX
        : el.align === "right"
          ? boxX + boxW - width
          : boxX + (boxW - width) / 2;
    return { text: t, width, x, baseline: boxY + i * lineAdvancePx + halfLeading + m.ascent * fontPx };
  });
  ctx.restore();
  return {
    face,
    font,
    fontPx,
    spacingPx,
    lineAdvancePx,
    lines,
    heightPx: lines.length * lineAdvancePx,
  };
}

/** Measuring resolution for heights: fine enough that rounding never matters. */
const MEASURE_PX_PER_MM = 20;

/**
 * The height (mm) a text element's wrapped text occupies. Browser: measured
 * with the real font; Node/SSR: one-line-per-paragraph estimate from the
 * fallback metrics (never used for drawing).
 */
export function measureTextHeightMm(el: TextElement): number {
  const ctx = getMeasureCtx();
  if (!ctx) {
    const m = getFont(el.font).metrics;
    const lines = Math.max(1, el.text.split("\n").length);
    return Math.round((el.size_mm / m.capHeight) * el.line_height * lines * 100) / 100;
  }
  const lay = layoutText(ctx, el, MEASURE_PX_PER_MM);
  return Math.round((lay.heightPx / MEASURE_PX_PER_MM) * 100) / 100;
}

// ---------------------------------------------------------------------------
// Element drawing
// ---------------------------------------------------------------------------

export interface DrawOptions {
  /** Elements not to draw (e.g. the text being edited in the overlay). */
  skipIds?: readonly string[];
  /**
   * Editor and template thumbnails only: draw a dashed placeholder (with the
   * slot's caption, if any) where an image has no decoded asset, and a dashed
   * box where an ornament is still decoding. Exports never set it.
   */
  placeholders?: boolean;
}

/** Placeholder ink on a light label (the M4 dashed-box colour). */
const PLACEHOLDER_INK_LIGHT = "rgba(128,112,90,0.75)";
/** Placeholder ink on a dark label (black, burgundy, forest, charcoal). */
const PLACEHOLDER_INK_DARK = "rgba(243,239,232,0.6)";

/** The placeholder ink that reads on a label background. */
export function placeholderInk(background: string): string {
  return luminance(background) < 0.25 ? PLACEHOLDER_INK_DARK : PLACEHOLDER_INK_LIGHT;
}

function drawDashedBox(ctx: Ctx, x: number, y: number, w: number, h: number, pxPerMm: number, ink: string) {
  ctx.save();
  ctx.strokeStyle = ink;
  ctx.lineWidth = Math.max(1, pxPerMm * 0.25);
  ctx.setLineDash([pxPerMm * 1.5, pxPerMm * 1]);
  ctx.strokeRect(x, y, w, h);
  ctx.restore();
}

/** An empty image slot's caption, centred in its box (Montserrat, wrapped). */
function drawSlotCaption(ctx: Ctx, el: ImageElement, pxPerMm: number, ink: string) {
  const text = el.placeholder?.trim();
  if (!text) return;
  const pad = Math.min(2, el.width * 0.08);
  const size = Math.max(1.4, Math.min(3, el.height * 0.09, el.width * 0.07));
  const caption: TextElement = {
    id: el.id,
    type: "text",
    x: el.x + pad,
    y: 0,
    width: Math.max(1, el.width - 2 * pad),
    height: 0,
    text,
    font: "montserrat",
    size_mm: size,
    color: ink,
    align: "center",
    weight: "regular",
    italic: false,
    letter_spacing_em: 0.06,
    line_height: 1.3,
  };
  const heightMm = layoutText(ctx, caption, pxPerMm).heightPx / pxPerMm;
  drawText(ctx, { ...caption, y: el.y + (el.height - heightMm) / 2 }, pxPerMm);
}

function drawText(ctx: Ctx, el: TextElement, pxPerMm: number) {
  if (!el.text) return;
  const lay = layoutText(ctx, el, pxPerMm);
  ctx.save();
  ctx.font = lay.font;
  ctx.fillStyle = el.color;
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  const native = supportsLetterSpacing(ctx);
  if (native) ctx.letterSpacing = `${lay.spacingPx}px`;
  for (const line of lay.lines) {
    if (!line.text) continue;
    if (native || lay.spacingPx === 0) {
      ctx.fillText(line.text, line.x, line.baseline);
    } else {
      let x = line.x;
      for (const g of glyphs(line.text)) {
        ctx.fillText(g, x, line.baseline);
        x += ctx.measureText(g).width + lay.spacingPx;
      }
    }
  }
  ctx.restore();
}

function drawImageEl(
  ctx: Ctx,
  el: ImageElement,
  pxPerMm: number,
  img: ImageSource | null | undefined,
  placeholders: boolean,
  ink: string,
) {
  const x = el.x * pxPerMm;
  const y = el.y * pxPerMm;
  const w = el.width * pxPerMm;
  const h = el.height * pxPerMm;
  if (!img) {
    if (!placeholders) return;
    drawDashedBox(ctx, x, y, w, h, pxPerMm, ink);
    drawSlotCaption(ctx, el, pxPerMm, ink);
    return;
  }
  const nw = "naturalWidth" in img ? img.naturalWidth : img.width;
  const nh = "naturalHeight" in img ? img.naturalHeight : img.height;
  if (!nw || !nh) return;
  // fit: contain
  const s = Math.min(w / nw, h / nh);
  const dw = nw * s;
  const dh = nh * s;
  ctx.save();
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
  ctx.restore();
}

function drawRect(ctx: Ctx, el: RectElement, pxPerMm: number) {
  const x = el.x * pxPerMm;
  const y = el.y * pxPerMm;
  const w = el.width * pxPerMm;
  const h = el.height * pxPerMm;
  ctx.save();
  if (el.fill) {
    ctx.fillStyle = el.fill;
    ctx.fillRect(x, y, w, h);
  }
  const s = el.stroke_mm * pxPerMm;
  if (s > 0) {
    ctx.strokeStyle = el.stroke;
    ctx.lineWidth = s;
    ctx.lineJoin = "miter";
    // Stroke sits inside the box.
    ctx.strokeRect(x + s / 2, y + s / 2, Math.max(0, w - s), Math.max(0, h - s));
  }
  ctx.restore();
}

function drawEllipse(ctx: Ctx, el: EllipseElement, pxPerMm: number) {
  const rx = (el.width * pxPerMm) / 2;
  const ry = (el.height * pxPerMm) / 2;
  const cx = el.x * pxPerMm + rx;
  const cy = el.y * pxPerMm + ry;
  const s = el.stroke_mm * pxPerMm;
  ctx.save();
  if (el.fill) {
    ctx.beginPath();
    ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
    ctx.fillStyle = el.fill;
    ctx.fill();
  }
  if (s > 0) {
    ctx.beginPath();
    ctx.ellipse(cx, cy, Math.max(0, rx - s / 2), Math.max(0, ry - s / 2), 0, 0, Math.PI * 2);
    ctx.strokeStyle = el.stroke;
    ctx.lineWidth = s;
    ctx.stroke();
  }
  ctx.restore();
}

function drawLine(ctx: Ctx, el: LineElement, pxPerMm: number) {
  ctx.save();
  ctx.fillStyle = el.stroke;
  const t = el.thickness_mm * pxPerMm;
  const cy = (el.y + el.height / 2) * pxPerMm;
  ctx.fillRect(el.x * pxPerMm, cy - t / 2, el.width * pxPerMm, t);
  ctx.restore();
}

/** Device px per canvas unit on each axis (the context's current transform). */
function deviceScale(ctx: Ctx): { x: number; y: number } {
  if (typeof ctx.getTransform !== "function") return { x: 1, y: 1 };
  const t = ctx.getTransform();
  return { x: Math.hypot(t.a, t.b) || 1, y: Math.hypot(t.c, t.d) || 1 };
}

/**
 * An ornament: its SVG decoded at the element's DEVICE-pixel size (so it is
 * crisp at any zoom and at 300 dpi), recoloured, drawn contained and centred
 * in the box. While the decode is pending the editor shows a dashed box.
 */
function drawOrnament(ctx: Ctx, el: OrnamentElement, pxPerMm: number, placeholders: boolean, ink: string) {
  const x = el.x * pxPerMm;
  const y = el.y * pxPerMm;
  const w = el.width * pxPerMm;
  const h = el.height * pxPerMm;
  if (w <= 0 || h <= 0) return;
  const d = deviceScale(ctx);
  const img = ornamentImage(el.asset, el.color, Math.max(w * d.x, h * d.y));
  if (!img) {
    if (placeholders) drawDashedBox(ctx, x, y, w, h, pxPerMm, ink);
    return;
  }
  const aspect = getOrnament(el.asset)?.aspect ?? w / h;
  let dw = w;
  let dh = w / aspect;
  if (dh > h) {
    dh = h;
    dw = h * aspect;
  }
  ctx.save();
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
  ctx.restore();
}

export function drawElement(
  ctx: Ctx,
  el: LabelElement,
  pxPerMm: number,
  assets: Pick<Assets, "uploadedImage">,
  placeholders = false,
  ink: string = PLACEHOLDER_INK_LIGHT,
): void {
  switch (el.type) {
    case "text":
      return drawText(ctx, el, pxPerMm);
    case "image":
      return drawImageEl(ctx, el, pxPerMm, assets.uploadedImage, placeholders, ink);
    case "rect":
      return drawRect(ctx, el, pxPerMm);
    case "ellipse":
      return drawEllipse(ctx, el, pxPerMm);
    case "line":
      return drawLine(ctx, el, pxPerMm);
    case "ornament":
      return drawOrnament(ctx, el, pxPerMm, placeholders, ink);
  }
}

/**
 * Draw the whole label with its top-left at the context's current origin,
 * at `pxPerMm`. Background first, then elements in array order, all clipped
 * to the label rectangle.
 */
export function drawLabel(
  ctx: Ctx,
  doc: CanvasData,
  pxPerMm: number,
  assets: Pick<Assets, "uploadedImage">,
  opts: DrawOptions = {},
): void {
  const w = doc.label.width_mm * pxPerMm;
  const h = doc.label.height_mm * pxPerMm;
  const skip = opts.skipIds && opts.skipIds.length ? new Set(opts.skipIds) : null;
  const ink = placeholderInk(doc.background.color);
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, w, h);
  ctx.clip();
  ctx.fillStyle = doc.background.color;
  ctx.fillRect(0, 0, w, h);
  for (const el of doc.elements) {
    if (skip?.has(el.id)) continue;
    drawElement(ctx, el, pxPerMm, assets, !!opts.placeholders, ink);
  }
  ctx.restore();
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

/** Flat label PNG at `dpi` (300 = print resolution). Not stored in M4. */
export async function renderLabelPng(
  doc: CanvasData,
  assets: Pick<Assets, "uploadedImage">,
  dpi = 300,
): Promise<Blob> {
  const k = dpiToPxPerMm(dpi);
  await Promise.all([loadFonts(doc), loadOrnaments(doc, k)]);
  const canvas = createCanvas(doc.label.width_mm * k, doc.label.height_mm * k);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D unavailable");
  // Rounding the canvas size can differ from w×k by < 1 px; scale to fill exactly.
  ctx.scale(canvas.width / (doc.label.width_mm * k), canvas.height / (doc.label.height_mm * k));
  drawLabel(ctx, doc, k, assets);
  return canvasToBlob(canvas);
}

// ---- Bottle composite ----------------------------------------------------------

/** Height of the small-print band under the bottle, in bottle-image px (× scale). */
export const SMALL_PRINT_BAND_PX = 34;

/** Output size (px) of a bottle composite at `scale` × the bottle image. */
export function bottleCompositeSize(bottle: BottleSpec, scale = 1): { width: number; height: number } {
  return {
    width: Math.round(bottle.image.width * scale),
    height: Math.round((bottle.image.height + SMALL_PRINT_BAND_PX) * scale),
  };
}

/**
 * Draw the bottle mock-up onto `ctx` with its top-left at the current origin,
 * at `scale` × the bottle image's pixel size:
 *  1. the (retouched, label-patch-free) bottle photograph;
 *  2. the label at true proportions in the bottles.json rectangle;
 *  3. a whisper of cylindrical shading at the label's left/right edges and a
 *     1-image-px darker inner edge, so it reads as paper on glass (the plan's
 *     flat composite — no warp in v1);
 *  4. a black band below the photo carrying the small print in Montserrat.
 * This is THE code path for both the live preview and the downloaded PNG.
 */
export function drawBottleComposite(
  ctx: Ctx,
  doc: CanvasData,
  assets: Assets,
  bottle: BottleSpec,
  scale = 1,
): void {
  const size = bottleCompositeSize(bottle, scale);
  const photoH = bottle.image.height * scale;
  const img = assets.bottleImages[doc.bottle];

  ctx.save();
  // 1. Photograph (or a neutral cellar tone while it decodes).
  if (img) {
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, 0, 0, size.width, photoH);
  } else {
    ctx.fillStyle = "#1b1715";
    ctx.fillRect(0, 0, size.width, photoH);
  }

  // 2. Label. The x and y scales agree to < 0.2 % (W0 built the rectangle at
  //    true proportion); draw at the x scale and stretch y by the residual.
  const ls = labelScale(bottle, doc.label);
  const rx = bottle.label.x * scale;
  const ry = bottle.label.y * scale;
  const rw = bottle.label.width * scale;
  const rh = bottle.label.height * scale;
  const pxPerMm = ls.x * scale;
  ctx.save();
  ctx.translate(rx, ry);
  ctx.scale(1, ls.y / ls.x);
  drawLabel(ctx, doc, pxPerMm, assets);
  ctx.restore();

  // 3. Paper-on-glass: soft edge shading + a darker 1 px (image px) inner edge.
  ctx.save();
  const g = ctx.createLinearGradient(rx, 0, rx + rw, 0);
  g.addColorStop(0, "rgba(0,0,0,0.16)");
  g.addColorStop(0.1, "rgba(0,0,0,0.04)");
  g.addColorStop(0.22, "rgba(0,0,0,0)");
  g.addColorStop(0.78, "rgba(0,0,0,0)");
  g.addColorStop(0.9, "rgba(0,0,0,0.05)");
  g.addColorStop(1, "rgba(0,0,0,0.2)");
  ctx.fillStyle = g;
  ctx.fillRect(rx, ry, rw, rh);
  const edge = Math.max(1, scale);
  ctx.strokeStyle = "rgba(20,14,8,0.32)";
  ctx.lineWidth = edge;
  ctx.strokeRect(rx + edge / 2, ry + edge / 2, rw - edge, rh - edge);
  ctx.restore();

  // 4. Small-print band.
  const bandH = size.height - photoH;
  ctx.fillStyle = PALETTE_HEX.black;
  ctx.fillRect(0, photoH, size.width, bandH);
  ctx.fillStyle = "rgba(201,168,106,0.45)"; // gold hairline
  ctx.fillRect(0, photoH, size.width, Math.max(1, scale * 0.75));
  const face = resolveFace("montserrat", "regular", false);
  const maxW = size.width - 24 * scale;
  let fontPx = 11 * scale;
  const tracking = 0.04;
  ctx.textBaseline = "middle";
  ctx.textAlign = "left";
  let w = 0;
  for (let i = 0; i < 12; i++) {
    ctx.font = cssFont(face, fontPx);
    if (supportsLetterSpacing(ctx)) ctx.letterSpacing = "0px";
    w = runWidth(ctx, SMALL_PRINT, tracking * fontPx);
    if (w <= maxW || fontPx <= 7 * scale) break;
    fontPx *= 0.94;
  }
  ctx.font = cssFont(face, fontPx);
  ctx.fillStyle = PALETTE_HEX["warm-white"];
  const sp = tracking * fontPx;
  const tx = (size.width - w) / 2;
  const ty = photoH + bandH / 2 + Math.max(0.5, scale * 0.5);
  if (supportsLetterSpacing(ctx)) {
    ctx.letterSpacing = `${sp}px`;
    ctx.fillText(SMALL_PRINT, tx, ty);
  } else {
    let x = tx;
    for (const ch of glyphs(SMALL_PRINT)) {
      ctx.fillText(ch, x, ty);
      x += ctx.measureText(ch).width + sp;
    }
  }
  ctx.restore();
}

export interface BottleRenderOptions {
  /**
   * Multiple of the bottle image's pixel size. Default 2 (≈ 880 × 3260 px):
   * the photograph is upsampled but the label type and small print are drawn
   * natively at that size, so they stay crisp in the download.
   */
  scale?: number;
}

/**
 * The downloadable / stored bottle mock-up (`rendered_preview`). Awaits the
 * fonts and ornaments the design uses. Needs `assets.bottleImages[doc.bottle]`
 * decoded.
 */
export async function renderBottlePng(
  doc: CanvasData,
  assets: Assets,
  bottle: BottleSpec,
  opts: BottleRenderOptions = {},
): Promise<Blob> {
  const scale = opts.scale ?? 2;
  const ls = labelScale(bottle, doc.label);
  await Promise.all([loadFonts(doc), loadOrnaments(doc, Math.max(ls.x, ls.y) * scale)]);
  const size = bottleCompositeSize(bottle, scale);
  const canvas = createCanvas(size.width, size.height);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D unavailable");
  drawBottleComposite(ctx, doc, assets, bottle, scale);
  return canvasToBlob(canvas);
}
